import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";

// Date Picker
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";

// Assets
import redeemedImg from "../../assets/redeem.svg";

// Translation
import { Star, StarBorder } from "@mui/icons-material";
import {
  Box,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { WALLET_API } from "../../lib/axios";

const LIMIT_FOR_1_DENOM = Number.MAX_SAFE_INTEGER;
const LIMIT_PER_DENOM = Number.MAX_SAFE_INTEGER;

export function RedemptionDialog({ ...props }) {
  const { t } = useTranslation("common");
  const { isOpen, isClose, profile } = props;
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [dayoffs, setDayOffs] = useState([]);
  const [rating, setRating] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([9, 10, 11, 12, 13, 14]);
  const [redemptionFee, setRedemptionFee] = useState(0);
  const [availableDenoms, setAvailableDenoms] = useState([]);
  const [disableSubmit, setDisableSubmit] = useState(false);

  useEffect(() => {
    WALLET_API.get("/holidays")
      .then((res) => {
        return res.data;
      })
      .then((res) => res.map((v) => (v.date ? dayjs(v.date) : null)))
      .catch(() => [])
      .then((res) => setDayOffs(res));
    WALLET_API.get("/account/redeem")
      .then((res) => res.data)
      .then((res) => setBookings(res))
      .catch(() => setBookings([]));

    // Fetch redemption fee
    WALLET_API.get("/fee/burn")
      .then((res) => res.data)
      .then(() => {
        setRedemptionFee(0.06); // Temporarily set to 6% until the API is ready
      })
      .catch(() => {
        setRedemptionFee(0.06); // Default to 6% on error
      });

    // Fetch available denoms
    WALLET_API.get("/physicalgold/list")
      .then((res) => res.data)
      .then((res) => {
        setAvailableDenoms(res);
      })
      .catch(() => {
        setAvailableDenoms([]);
      });
  }, [navigate]);

  const handleBookingDateChange = (newdob) => {
    const bookingDate = dayjs(newdob);
    const slots = [9, 10, 11, 12, 13, 14].filter((x) => {
      const booked = bookings
        .filter((v) => dayjs(bookingDate).isSame(v.appointmentDate, "day"))
        .map((v) => v.timeSlot);
      return !booked.includes(x);
    });
    setAvailableSlots(slots);
    return setFormData({
      ...formData,
      appointmentDate: newdob,
    });
  };

  const defaultForm = {
    walletaddress: !profile.walletaddress ? "-" : profile.walletaddress,
    email: !profile.email ? "-" : profile.email,
    appointmentDate: dayjs(null),
  };

  const [formData, setFormData] = useState(defaultForm);
  const [physicalGold, setPhysicalgold] = useState({});
  const [redemptionAmount, setRedemptionAmount] = useState(0);
  const handleChangeRedemptionAmount = (
    denomID,
    denomAmount,
    amount,
    maxAmount
  ) => {
    const newphysgold = { ...physicalGold };
    if (amount > Math.min(LIMIT_PER_DENOM, maxAmount) && denomAmount > 1) {
      newphysgold[denomID] = {
        amount: Math.min(LIMIT_PER_DENOM, maxAmount),
        denom: denomAmount,
      };
    } else if (
      denomAmount === 1 &&
      amount > Math.min(LIMIT_FOR_1_DENOM, maxAmount)
    ) {
      newphysgold[denomID] = {
        amount: Math.min(LIMIT_FOR_1_DENOM, maxAmount),
        denom: denomAmount,
      };
    } else if (amount < 0) {
      newphysgold[denomID] = {
        amount: 0,
        denom: denomAmount,
      };
    } else {
      newphysgold[denomID] = {
        amount: amount,
        denom: denomAmount,
      };
    }
    setPhysicalgold(newphysgold);
    setRedemptionAmount(
      Object.keys(newphysgold).reduce(
        (acc, key) =>
          acc +
          (newphysgold[key]?.denom ?? 0) * (newphysgold[key]?.amount ?? 0),
        0
      )
    );
  };

  const [timeSlot, setTimeSlot] = useState("");
  const [npsRating, setNps] = React.useState(0);
  const [csatRating, setCsat] = React.useState(0);

  const handleChangeTimeSlot = (e) => {
    const newTime = e.target.value;
    setTimeSlot(newTime);
    setFormData({
      appointmentDate: dayjs(formData.appointmentDate).set(
        "hour",
        Number(newTime)
      ),
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setDisableSubmit(true);
    const toSend = {
      ...formData,
      physicalGold: Object.keys(physicalGold).map((key) => ({
        denomID: isNaN(Number(key)) ? 0 : Number(key),
        amount: isNaN(Number(physicalGold[key].amount))
          ? 0
          : Number(physicalGold[key].amount),
      })),
      timeSlot,
    };
    WALLET_API.post("/account/redeem/request", toSend)
      .then(() => {
        setRating(true);
      })
      .catch((err) => {
        if (err.response?.status === 406) {
          navigate("/?err=insufficientredeem");
          navigate(0);
          return;
        }
        if (err.response?.status === 409) {
          setRating(true);
          return;
        }
        navigate("/?err=redeem");
        navigate(0);
      })
      .finally(() => {
        setDisableSubmit(false);
      });
  };

  const handleRatingSubmit = (e) => {
    e.preventDefault();
    WALLET_API.post("/rating", {
      csat: csatRating,
      nps: npsRating,
    }).finally(() => {
      navigate("/?msg=redeem");
      navigate(0);
    });
  };

  const isDayOff = (date) => {
    const day = date.day();

    return (
      day === 0 ||
      day === 6 ||
      dayoffs.find((v) => dayjs(date).isSame(v, "day"))
    );
  };

  function RedemptionInfoStepper() {
    const { t } = useTranslation("common");

    return (
      <Box sx={{ width: "100%" }}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={12}>
            <Stepper
              sx={{
                "& .MuiStepConnector-line": {
                  minHeight: "15px",
                },
              }}
              orientation="vertical"
            >
              <Step active>
                <StepLabel>{t("rating.step1")}</StepLabel>
                <StepContent>
                  <Stack
                    direction={"row"}
                    justifyContent={"center"}
                    sx={{ my: 4 }}
                  >
                    {[1, 2, 3, 4, 5].map((x, i) => (
                      <Button key={i} onClick={() => setNps(x)}>
                        {x <= npsRating ? <Star /> : <StarBorder />}
                      </Button>
                    ))}
                  </Stack>
                </StepContent>
              </Step>
              <Step active>
                <StepLabel>{t("rating.step2")}</StepLabel>
                <StepContent>
                  <Stack
                    direction={"row"}
                    justifyContent={"center"}
                    sx={{ my: 4 }}
                  >
                    {[1, 2, 3, 4, 5].map((x, i) => (
                      <Button key={i} onClick={() => setCsat(x)}>
                        {x <= csatRating ? <Star /> : <StarBorder />}
                      </Button>
                    ))}
                  </Stack>
                </StepContent>
              </Step>
            </Stepper>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return rating ? (
    <Dialog
      fullWidth
      open={isOpen}
      onClose={isClose}
      maxWidth={"sm"}
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Typography variant="h4" fontWeight="600">
          {t("redeemGIDR.modal.description")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12} md={12}>
            <RedemptionInfoStepper />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleRatingSubmit}>Submit</Button>
      </DialogActions>
    </Dialog>
  ) : (
    <Dialog
      fullWidth
      open={isOpen}
      onClose={isClose}
      maxWidth={"sm"}
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Typography variant="h4" fontWeight="600">
          {t("redeemGIDR.modal.title")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1} sx={{ mt: 2 }}>
          <Grid item xs={12} md={12} sx={{ textAlign: "center", mb: 1 }}>
            <img src={redeemedImg} width="220px" alt="GIDR - Redeemed" />
          </Grid>
          <Grid item xs={12} md={12} sx={{ mb: 1 }}>
            <Typography variant="body1" fontWeight="600">
              {t("redeemGIDR.modal.accountInformation.title")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <Typography variant="body2" fontWeight="600" sx={{ mb: "8px" }}>
                  {t("redeemGIDR.modal.accountInformation.walletAddress")}
                </Typography>
                <Typography variant="body2" color="#7e7e7e">
                  {profile.walletaddress}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  sx={{ mb: "8px", fontWeight: "600" }}
                >
                  {t("redeemGIDR.modal.accountInformation.fullname")}
                </Typography>
                <Typography variant="body2" color="#7e7e7e">
                  {profile.fullname}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  sx={{ mb: "8px", fontWeight: "600" }}
                >
                  {t("redeemGIDR.modal.accountInformation.emailAddress")}
                </Typography>
                <Typography variant="body2" color="#7e7e7e">
                  {profile.email}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  sx={{ mb: "8px", fontWeight: "600" }}
                >
                  {t("redeemGIDR.modal.accountInformation.kycStatus")}
                </Typography>
                <Chip
                  label={
                    profile.statuskyc !== "UNVERIFIED"
                      ? t("kycStatus.verified")
                      : t("kycStatus.notVerified")
                  }
                  color={
                    profile.statuskyc !== "UNVERIFIED" ? "success" : "error"
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ mb: "8px", fontWeight: "600" }}
                >
                  {t("redeemGIDR.modal.collectionDate.title")}
                </Typography>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={"en-gb"}
                >
                  <DatePicker
                    onChange={handleBookingDateChange}
                    disablePast
                    shouldDisableDate={isDayOff}
                    minDate={dayjs().add(3, "day")}
                    name="collectionDate"
                    id="collectionDate"
                    value={formData.appointmentDate || ""}
                    placeholder={t(
                      "redeemGIDR.modal.collectionDate.placeholder"
                    )}
                    slotProps={{
                      day: (day) => {
                        const isHolidayDay = isDayOff(day.day);
                        return {
                          sx: isHolidayDay ? { color: "red!important" } : {},
                        };
                      },
                      textField: {
                        fullWidth: true,
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "50px",
                        fontSize: "14px",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ mb: "8px", fontWeight: "600" }}
                >
                  {t("redeemGIDR.modal.accountInformation.timeSlot")}
                </Typography>
                <FormControl fullWidth>
                  <Select
                    disabled={!formData.appointmentDate.isValid()}
                    value={timeSlot}
                    fullWidth
                    displayEmpty
                    onChange={handleChangeTimeSlot}
                    style={{ height: "50px", fontSize: "14px" }}
                  >
                    <MenuItem value="">
                      <em>
                        {!formData.appointmentDate.isValid()
                          ? t(
                              "redeemGIDR.modal.accountInformation.timeSlotPlaceholderEmptyDate"
                            )
                          : availableSlots.length === 0
                          ? "No slots available"
                          : t(
                              "redeemGIDR.modal.accountInformation.timeSlotPlaceholder"
                            )}
                      </em>
                    </MenuItem>
                    {availableSlots.map((x, i) => {
                      return (
                        <MenuItem value={x} key={i}>
                          {x >= 10 ? x.toString() : "0" + x}:00 - {x + 1}:00 WIB
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ mb: "8px", fontWeight: "600" }}
                >
                  {t("redeemGIDR.modal.accountInformation.redemptionAmount")}
                </Typography>
                <Grid container spacing={1}>
                  {availableDenoms
                    .sort((a, b) => b.denom - a.denom)
                    .map((x, i) => {
                      if (x.amount <= 0) {
                        return null;
                      }
                      return (
                        <Grid item xs={12} md={4} key={i}>
                        <Typography variant="h4" color="#7e7e7e">
                          Kepingan {x.denom} gram
                        </Typography>
                        <TextField
                          value={physicalGold[x.id]?.amount ?? 0}
                          onChange={(e) =>
                            handleChangeRedemptionAmount(
                              x.id,
                              x.denom,
                              e.target.value,
                              x.amount
                            )
                          }
                          type="number"
                          disabled={x.amount === 0}
                          helperText={
                            x.amount > 0 &&
                            `Max: ${
                              x.denom === 1
                                ? x.amount > LIMIT_FOR_1_DENOM
                                  ? LIMIT_FOR_1_DENOM
                                  : x.amount
                                : x.amount > LIMIT_PER_DENOM
                                ? LIMIT_PER_DENOM
                                : x.amount
                            }`
                          }
                          fullWidth
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  sx={{ mb: "8px", fontWeight: "600" }}
                >
                  {t("redeemGIDR.modal.totalRedeemed")}
                </Typography>
                <Typography variant="body2" color="#7e7e7e">
                  {redemptionAmount} GIDR
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  sx={{ mb: "8px", fontWeight: "600" }}
                >
                  {t("redeemGIDR.modal.redemptionFee")}
                </Typography>
                <Typography variant="body2" color="#7e7e7e">
                  {Math.round(redemptionAmount * redemptionFee * 100) / 100} GIDR
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="body2"
                  sx={{ mb: "8px", fontWeight: "600" }}
                >
                  {t("redeemGIDR.modal.totalGIDRPaid")}
                </Typography>
                <Typography variant="body2" color="#7e7e7e">
                  {Math.round(redemptionAmount * (1 + redemptionFee) * 100) / 100} GIDR
                </Typography>
              </Grid>
              <Grid item xs={12} md={12} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  disabled={
                    redemptionAmount === "" ||
                    redemptionAmount * (1 + redemptionFee) > profile.balance ||
                    disableSubmit
                  }
                  sx={{ fontWeight: "600", boxShadow: "unset" }}
                  fullWidth
                  onClick={handleFormSubmit}
                >
                  {t("redeemGIDR.modal.accountInformation.button")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={isClose}>{t("redeemGIDR.modal.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
